import { t, useLanguage } from "@/lib/i18n";
import React, { useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ChangePassword() {
  useLanguage();
  const { user, ready, logout } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token");
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const perform = async (action) => {
    setBusy(true); setMessage(""); setError("");
    try { await action(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Please try again."); }
    finally { setBusy(false); }
  };
  const request = () => perform(async () => {
    const { data } = await api.post("/auth/password-change/request");
    setMessage(data.message);
  });
  const submit = (event) => {
    event.preventDefault();
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    perform(async () => {
      const { data } = await api.post("/auth/password-change/confirm", { token, password });
      setMessage(data.message); setDone(true); setPassword(""); setConfirmPassword("");
      await logout();
    });
  };
  return <main className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-5">
    <section className="w-full max-w-md bg-white border rounded-xl p-6 space-y-5">
      <h1 className="font-serif text-3xl">{t("Change password")}</h1>
      {message && <p role="status" className="text-sm text-green-800">{t(message)}</p>}
      {error && <p role="alert" className="text-sm text-red-700">{t(error)}</p>}
      {done ? <Link className="text-amber-800 underline" to="/login">{t("Sign in with your new password")}</Link>
        : !ready ? <p>{t("Loading your account...")}</p>
        : !user || user.role === "guest" ? <>
          <p>{t("Sign in to the account that requested the password change.")}</p>
          <Link className="text-amber-800 underline" to="/login" state={{ returnTo: location.pathname + location.search }}>{t("Sign in")}</Link>
        </> : !user.email_verified ? <>
          <p>{t("Verify your email before changing your password.")}</p>
          <Link className="text-amber-800 underline" to="/verify-email">{t("Verify email")}</Link>
        </> : <>
          <p className="text-sm text-stone-600">{t("Confirm this change using a link sent to ")}{user.email}{t(". Links expire in 30 minutes and can be used once.")}</p>
          {token && <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2"><Label htmlFor="new-password">{t("New password")}</Label>
              <Input id="new-password" type="password" autoComplete="new-password" minLength={8} maxLength={72} required value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t("At least 8 characters")} /></div>
            <div className="space-y-2"><Label htmlFor="confirm-new-password">{t("Confirm new password")}</Label>
              <Input id="confirm-new-password" type="password" autoComplete="new-password" required value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} /></div>
            <Button type="submit" disabled={busy} className="w-full">{t("Change password")}</Button>
          </form>}
          <Button variant="outline" disabled={busy} onClick={request} className="w-full">{token ? t("Send a new confirmation link") : t("Send password change email")}</Button>
          <Link className="block text-amber-800 underline" to="/app">{t("Back to tree")}</Link>
        </>}
    </section>
  </main>;
}
